import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Printer, Loader2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';

const SETTIMANE = [1, 2, 3, 4];

export default function ChecklistTab() {
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [dati, setDati] = useState(null);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDati = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get('/moduli/checklist', { params: { mese } }),
        api.get('/moduli/checklist/alert'),
      ]);
      setDati(c.data); setAlert(a.data);
    } catch { toast.error('Errore caricamento checklist'); }
    setLoading(false);
  }, [mese]);

  useEffect(() => { fetchDati(); }, [fetchDati]);

  const toggle = async (voce, settimana, fatto) => {
    try {
      await api.put('/moduli/checklist', { voce, settimana, mese, fatto: !fatto });
      fetchDati();
    } catch { toast.error('Errore salvataggio'); }
  };

  const stampa = () => {
    if (!dati) return;
    const blocco = (titolo, righe) => {
      let html = `<div class="section-title">${titolo}</div><table><thead><tr><th>Voce</th>${SETTIMANE.map(s => `<th style="text-align:center">${s}ª sett.</th>`).join('')}</tr></thead><tbody>`;
      righe.forEach(r => {
        html += `<tr><td>${r.voce}</td>${SETTIMANE.map(s => `<td style="text-align:center">${r.settimane[s].fatto ? 'X' : ''}</td>`).join('')}</tr>`;
      });
      return html + '</tbody></table>';
    };
    openPrintWindow('Checklist Carrello d\'Emergenza',
      blocco('Materiale', dati.materiali) + blocco('Controlli', dati.controlli),
      { subtitle: `Mese ${mese}` });
  };

  if (loading && !dati) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  const Riga = ({ r }) => (
    <tr className="hover:bg-slate-50" data-testid={`checklist-row-${r.voce.slice(0, 12)}`}>
      <td className="px-3 py-1.5 border-b border-slate-100 text-slate-700">{r.voce}</td>
      {SETTIMANE.map(s => {
        const cella = r.settimane[s];
        return (
          <td key={s} className="border-b border-slate-100 text-center p-1">
            <button onClick={() => toggle(r.voce, s, cella.fatto)}
              title={cella.operatore ? `${cella.operatore} il ${cella.quando}` : 'Non controllato'}
              className={`w-8 h-7 rounded border text-[11px] font-bold transition-colors ${cella.fatto
                ? 'bg-brand-soft border-brand/40 text-brand-dark' : 'bg-white border-slate-200 text-slate-300 hover:border-brand'}`}
              data-testid={`checklist-cell-${r.voce.slice(0, 12)}-${s}`}>
              {cella.fatto ? '✓' : ''}
            </button>
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="space-y-4" data-testid="checklist-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={mese} onChange={e => setMese(e.target.value)} className="w-40 h-9" data-testid="checklist-mese" />
        <Badge className="bg-brand-soft text-brand-dark">{dati.settimana_corrente}ª settimana del mese</Badge>
        <Button size="sm" variant="outline" onClick={stampa} className="h-9" data-testid="checklist-print">
          <Printer className="w-4 h-4 mr-1" />Stampa
        </Button>
      </div>

      {alert && !alert.completa && (
        <Card className="border-amber-200 bg-amber-50" data-testid="checklist-alert">
          <CardContent className="p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              Carrello d'emergenza: <strong>{alert.mancanti} voci su {alert.totale}</strong> non ancora
              controllate per la {alert.settimana}ª settimana di questo mese.
            </p>
          </CardContent>
        </Card>
      )}
      {alert?.completa && (
        <Card className="border-brand/30 bg-brand-soft/40">
          <CardContent className="p-3 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-brand" />
            <p className="text-xs text-brand-dark">Carrello d'emergenza controllato completamente per questa settimana.</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Materiale carrello d'emergenza</th>
                {SETTIMANE.map(s => <th key={s} className="px-2 py-2 text-center">{s}ª sett.</th>)}
              </tr>
            </thead>
            <tbody>{dati.materiali.map(r => <Riga key={r.voce} r={r} />)}</tbody>
            <thead className="bg-slate-50">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Controlli</th>
                {SETTIMANE.map(s => <th key={s} className="px-2 py-2 text-center">{s}ª sett.</th>)}
              </tr>
            </thead>
            <tbody>{dati.controlli.map(r => <Riga key={r.voce} r={r} />)}</tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
